import useTranslate from "../../../core/languages/hooks/useTranslate";
import React, { useEffect, useRef, useState } from "react";
import ClearDataButton from "../../../common/components/filters/ClearDataButton";
import { BookingFilters } from "../../hooks/useBookingMeSearchData";
import { useHotelMeSearchData } from "../../hooks/useHotelMeSearchData";

interface Props {
    onChange: (updates: Partial<BookingFilters>) => void;
    hotels: { id: string | number; name: string }[];
}

const HotelFilter: React.FC<Props> = ({ onChange, hotels = [] }) => {
    const t = useTranslate();
    const hotelRef = useRef<HTMLDivElement | null>(null);
    const [showHotelPopover, setShowHotelPopover] = useState(false);
    const [selectedHotel, setSelectedHotel] = useState<string | null>(null);
    const { data: hotelObjects, loading, fetchData } = useHotelMeSearchData();

    const handleSelectHotel = (value: string | null) => {
        setSelectedHotel(value);
        setShowHotelPopover(false);

        if (value) {
            onChange({ hotel: value });
        } else {
            onChange({ hotel: undefined });
        }
    };

    const handleClickOutside = (e: MouseEvent) => {
        if (hotelRef.current && !hotelRef.current.contains(e.target as Node)) {
            setShowHotelPopover(false);
        }
    };

    useEffect(() => {
        document.addEventListener("click", handleClickOutside);
        return () => document.removeEventListener("click", handleClickOutside);
    }, []);

    useEffect(() => {
        if (!hotels || hotels.length === 0) return;

        fetchData({ hotel: hotels });
    }, [hotels, fetchData]);

    const renderPopoverOptions = (
        options: { label: string; value: string | number }[],
        onSelect: (value: string | null) => void
    ) =>
        options.map((opt) => (
            <div
                key={String(opt.value)}
                className="flex px-4 sm:px-8 items-center py-4 hover:bg-neutral-100 dark:hover:bg-neutral-700 cursor-pointer"
                onClick={() => onSelect(String(opt.value))}
            >
                <span className="block font-medium text-neutral-700 dark:text-neutral-200">
                    {opt.label}
                </span>
            </div>
        ));

    const optionsHotels = (hotelObjects ?? []).map((e) => ({
        label: e?.name ?? "Sin nombre",
        value: e?.id ?? "",
    }));

    return (
        <div
            ref={hotelRef}
            className="relative w-60 cursor-pointer [ nc-hero-field-padding ] items-center min-w-0"
        >
            <div className="flex items-start">
                <div className="flex-grow min-w-0">
                    <div
                        className="w-full p-0 xl:text-lg font-semibold min-w-0 cursor-pointer text-neutral-800 dark:text-neutral-100"
                        onClick={() => setShowHotelPopover((v) => !v)}
                    >
                        {selectedHotel ? (
                            <span className="whitespace-normal break-words block">
                                {(hotelObjects ?? []).find((e) => String(e.id) === selectedHotel)?.name ??
                                    t("bookingMeForm.hotel")}
                            </span>
                        ) : (
                            <span className="whitespace-normal break-words block text-neutral-800 dark:text-neutral-200">
                                {t("bookingMeForm.hotel")}
                            </span>
                        )}
                    </div>
                    <span className="block mt-0.5 text-sm text-neutral-400 font-light">
                        {t("bookingMeForm.searchByHotel")}
                    </span>
                </div>

                {selectedHotel && (
                    <div className="ml-2">
                        <ClearDataButton onClick={() => handleSelectHotel(null)} />
                    </div>
                )}
            </div>

            {showHotelPopover && !loading && (
                <div className="absolute left-0 z-40 w-full bg-white dark:bg-neutral-800 top-full mt-3 py-3 rounded-3xl shadow-xl max-h-60 overflow-y-auto">
                    {renderPopoverOptions(optionsHotels, handleSelectHotel)}
                </div>
            )}
        </div>
    );
};

export default HotelFilter;
